import { useCallback } from "react";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";

import { md, useBreakpoints, useFormAnalytics } from "@hmn/coolinarika-web-core/hooks";

import { Button, ButtonsCol, buttonSizes, buttonVariants } from "../../../Button";
import { DrawerBody, DrawerFooter } from "../../../Drawer";
import { CheckMarkIcon, EraseIcon, Icon } from "../../../Icon";
import { TextArea } from "../../../Input/components/TextArea";
import { TextField } from "../../../Input/components/TextField";
import { Column } from "../../../Layout";

const Editor = ({ collection, onUpdate, onDelete, isLoading }) => {
    const [isMobile] = useBreakpoints(md);

    const { eventWithTrackingData } = useFormAnalytics({
        trackingData: {
            elementLabel: "Edit collection",
        },
    });

    // Validate form
    const validateForm = (values) => {
        const errors = {};

        if (!values.title) {
            errors.title = "Ime kolekcije ne može biti prazno";
        }

        return errors;
    };

    const onSubmit = useCallback(
        (values) => {
            onUpdate(values);
            eventWithTrackingData();
        },
        [collection, onUpdate, eventWithTrackingData],
    );

    const handleDelete = useCallback(() => {
        onDelete();
    }, [collection, onDelete]);

    if (!collection) {
        return null;
    }

    return (
        <Formik
            initialValues={{
                id: collection.id,
                title: collection.title,
                description: collection.description,
            }}
            validate={validateForm}
            onSubmit={onSubmit}
        >
            {({ values, errors, handleBlur, handleChange }) => (
                <Form className="u-formFullHeight">
                    <DrawerBody>
                        <Column variant="form.column">
                            <TextField
                                label="Naziv kolekcije"
                                name="title"
                                type="text"
                                value={values.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.title}
                                errorMessage={errors.title}
                            />
                        </Column>
                        <Column variant="form.column" mb={0}>
                            <TextArea
                                label="Opis kolekcije"
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                noResize
                            />
                        </Column>
                    </DrawerBody>

                    <DrawerFooter hasSmallSpacing>
                        <ButtonsCol>
                            <Button
                                type="submit"
                                title="Spremi"
                                variant={buttonVariants.QUATERNARY}
                                size={buttonSizes.LARGE}
                                sizeMobile={buttonSizes.NORMAL}
                                width="100%"
                                widthMobile="0"
                                disabled={isLoading}
                                iconLeftComponent={<Icon icon={CheckMarkIcon} />}
                            >
                                Spremi
                            </Button>
                            <Button
                                title="Obriši kolekciju"
                                variant={buttonVariants.SECONDARY}
                                size={buttonSizes.NORMAL}
                                onClick={handleDelete}
                                simple={isMobile}
                                disabled={isLoading}
                                iconLeftComponent={<Icon icon={EraseIcon} />}
                                className="editorForm_btnDelete"
                            >
                                {isMobile ? "Obriši kolekciju" : "Obriši"}
                            </Button>
                        </ButtonsCol>
                    </DrawerFooter>
                </Form>
            )}
        </Formik>
    );
};

Editor.propTypes = {
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    collection: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        description: PropTypes.string,
    }),
    isLoading: PropTypes.bool,
};

Editor.defaultProps = {
    onUpdate: () => {},
    onDelete: () => {},
    collection: undefined,
    isLoading: false,
};

export default Editor;
